/**
 * The clinician-editable Clinical Task lifecycle states, each carrying a FHIR
 * `Task.status`. `draft` is the pending-review state; the rest are decided.
 * Browser + backend read this one definition (contract validation, edit-sheet
 * select, card badge, the status→FHIR fold below).
 */
export const CLINICAL_TASK_STATUSES = [
  'draft',
  'requested',
  'in-progress',
  'completed',
  'cancelled',
] as const;

export type ClinicalTaskStatus = (typeof CLINICAL_TASK_STATUSES)[number];

/** Human-facing label per status — the edit-sheet Status select + card badge. */
export const CLINICAL_TASK_STATUS_LABELS: Record<ClinicalTaskStatus, string> = {
  draft: 'Draft',
  requested: 'To do',
  'in-progress': 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** Narrow a (possibly unknown) stored FHIR status to a known lifecycle state,
 *  or null. */
export const asClinicalTaskStatus = (
  value: string | null | undefined,
): ClinicalTaskStatus | null =>
  value && (CLINICAL_TASK_STATUSES as readonly string[]).includes(value)
    ? (value as ClinicalTaskStatus)
    : null;

/**
 * The FHIR `intent` and review-status tag CODE that fold a lifecycle status
 * into the review model: only a `draft` is still pending review (proposal /
 * pending); every decided state is attested (order). The tag SYSTEM url is
 * applied at the write site — it's a cross-agent framework marker, not minted
 * in this agent-owned lib. Cancelled keeps `order`: it's a tombstone hidden
 * from every list, so intent is immaterial.
 */
export const clinicalTaskIntentFor = (
  status: ClinicalTaskStatus,
): 'proposal' | 'order' => (status === 'draft' ? 'proposal' : 'order');

/** Review-status tag code for a lifecycle status: only a draft is pending. */
export const clinicalTaskReviewTagCodeFor = (
  status: ClinicalTaskStatus,
): 'pending' | 'attested' => (status === 'draft' ? 'pending' : 'attested');
